import {
    Button,
    FormControl,
    FormGroup,
    Paper,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress'
import Form from '@rjsf/mui'
import { FieldProps, RJSFSchema, RJSFValidationError } from '@rjsf/utils/dist'
import { JSONSchema7Object } from 'json-schema'
import _ from 'lodash'
import React, { ChangeEvent, useContext, useEffect, useState } from 'react'

import { styled } from '@material-ui/core'
import { IChangeEvent } from '@rjsf/mui/dist'
import { UiSchema } from '@rjsf/utils'
import validator from '@rjsf/validator-ajv8'
import { useUpdateNodeMutation } from 'apollo/configurator/mutations/UpdateNode.generated'
import { useConfiguratorPresetQuery } from 'apollo/configurator/queries/ConfiguratorPreset.generated'
import { useHasNodesOfPresetsQuery } from 'apollo/configurator/queries/HasNodesOfPresets.generated'
import { useModelSchemaForModelQuery } from 'apollo/configurator/queries/ModelSchemaForModel.generated'
import { useNodeQuery } from 'apollo/configurator/queries/Node.generated'
import { useUiSchemaQuery } from 'apollo/configurator/queries/UiSchema.generated'
import WarningDialog from 'components/modelManager/molecules/popups/WarningDialog'
import DependencyPathSelector from 'components/PathTree/DependencyPathSelector'
import { ErrorContext } from 'context/error/ErrorContext'
import { GalleryContext } from 'context/gallery/GalleryContext'
import { ModelContext } from 'context/model/ModelContext'
import { useTranslate } from 'react-admin'
import { getSchemaByNode } from 'utils/Schema'
import { lightTheme } from '../../../../layout/theme/LightTheme'
import { wrapAccordionAroundArrayFields } from './helpers/ArrayAccordionHelpers'

interface IAttributedEditor {
    id: number
    parentId: string | undefined
}

const affectedPresetsTableColumns = ['ID', 'Internal name', 'Published', 'Published code']

const StyledForm = styled(Form)({
    display: 'flex',
    flexDirection: 'column-reverse',
    alignItems: 'flex-start',
    paddingBottom: 64,

    '& > .MuiFormControl-root': {
        height: '100%',
        overflowY: 'scroll',
        overflowX: 'hidden',
        maxHeight: 'calc(100vh - 360px)',
        borderTop: '1px solid #eaeaea'
    },

    '& .MuiCheckbox-root': {
        padding: '0 8px 0 8px'
    },
    '& .MuiBox-root': {
        marginTop: 0
    }
})

const AffectedPresetTable = ({ affectedPresetsIDs }) => (
    <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="Affected presets table">
            <TableHead>
                <TableRow>
                    {affectedPresetsTableColumns.map((item) => (
                        <TableCell>{item}</TableCell>
                    ))}
                </TableRow>
            </TableHead>
            <TableBody>
                {affectedPresetsIDs.map((id) => (
                    <AffectedPresetTableRow id={id?.preset_id} />
                ))}
            </TableBody>
        </Table>
    </TableContainer>
)

const AffectedPresetTableRow = ({ id }) => {
    const { data: affectedPreset, loading } = useConfiguratorPresetQuery({
        variables: {
            configuratorPresetId: id
        }
    })

    if (loading)
        return (
            <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                {[...Array(affectedPresetsTableColumns.length)].map((x, i) => (
                    <TableCell>
                        <Skeleton animation="wave" key={i} />
                    </TableCell>
                ))}
            </TableRow>
        )

    return (
        <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
            <TableCell component="th" scope="row">
                {affectedPreset?.configuratorPreset?.id}
            </TableCell>
            <TableCell>{affectedPreset?.configuratorPreset?.internal_name}</TableCell>
            <TableCell>{affectedPreset?.configuratorPreset?.published ? '✅' : '❌'}</TableCell>
            <TableCell>{affectedPreset?.configuratorPreset?.published_code}</TableCell>
        </TableRow>
    )
}

const AttributesEditor = ({ id, parentId }: IAttributedEditor) => {
    const translate = useTranslate()
    const { data: { configuratorNode: node } = {}, loading } = useNodeQuery({
        variables: { id: id as any }
    })
    const { data: { configuratorUiSchema: uiSchema } = {}, loading: uiSchemaLoading } =
        useUiSchemaQuery()
    const { state: modelState, setState: setModelState } = useContext(ModelContext)
    const { data: { configuratorModelSchema: modelSchema } = {} } = useModelSchemaForModelQuery({
        variables: { model: modelState.currentModelCode }
    })

    const [updateNode] = useUpdateNodeMutation({
        variables: { id: 0 as any, node_attributes: '' }
    })
    const modelSchemaObject = modelSchema && JSON.parse(modelSchema)
    const body = node && JSON.parse(node?.node_attributes || '')
    const nodeSchema =
        modelSchemaObject &&
        node &&
        getSchemaByNode(modelSchemaObject, body.type, false, node.parent_id === null)
    const [formData, setFormData] = useState<unknown>({})
    const [uniqueKey, setUniqueKey] = useState<number>(0)
    const [formEvent, setFormEvent] = useState<IChangeEvent<any, RJSFSchema, any> | undefined>()

    const { setErrorState } = useContext(ErrorContext)

    const [isActiveAffectedPresetsWarningPopup, setIsActiveAffectedPresetsWarningPopup] =
        useState(false)
    const { data: hasNodesOfPresets } = useHasNodesOfPresetsQuery({
        variables: {
            configuratorNodeId: node?.id
        }
    })

    // Filter uniq affected presets
    const affectedPresetsIDs = _.uniqBy(
        hasNodesOfPresets?.configuratorNode?.has_nodes_of_presets,
        'preset_id'
    )

    const submitForm = (e: IChangeEvent<any, RJSFSchema, any>) => {
        const variables: any = {
            id,
            node_attributes: JSON.stringify(e.formData)
        }
        if (typeof parentId == 'number') {
            variables.parent_id = parentId
        }
        updateNode({
            variables: variables
        })

        setModelState({
            ...modelState,
            updatedModel: true,
            hasChanges: true,
            hasEdited: true,
            showEditedMessage: true
        })

        // Reset the edited message but keep al the changes, necessary for export button state
        setTimeout(() => {
            return setModelState({
                ...modelState,
                updatedModel: true,
                hasChanges: true,
                hasEdited: true,
                showEditedMessage: false
            })
        }, 1000)
    }

    const handleCloseAffectedPresetsWarningPopup = () => {
        setIsActiveAffectedPresetsWarningPopup(false)
    }

    const handleContinueAffectedPresetsWarningPopup = () => {
        if (formEvent) submitForm(formEvent)
        setIsActiveAffectedPresetsWarningPopup(false)
    }

    const IconFieldTemplate = (props: FieldProps) => {
        const { state, setGalleryState } = useContext(GalleryContext)
        const [files, setFiles] = useState({ ...props.formData })
        const [selectedInput, setSelectedInput] = useState<string>()
        const idSchemaArray: string[] = []

        const formatFileName = (file: string) =>
            file.includes('//') ? file.replace('//', '') : file.replace('/', '')

        // Fetch all the different icon states that are available
        Object.keys(props.idSchema).forEach((key: string) => {
            if (key !== '$id') {
                idSchemaArray.push(key)
            }
        })

        useEffect(() => {
            if (!state.openGallery && state.selectedFile && selectedInput) {
                setFiles({
                    ...files,
                    [selectedInput]: formatFileName(state.selectedFile)
                })
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [state])

        useEffect(() => {
            // Return the changed filenames to the json schema
            if (files) {
                return props.onChange(files)
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [files, selectedInput])

        const handleChange = (name: string, event: ChangeEvent<HTMLInputElement>) => {
            return setFiles({
                ...files,
                [name]: formatFileName(event.target.value)
            })
        }

        return (
            <React.Fragment>
                <h3>{props?.schema.title}</h3>
                <FormControl fullWidth={true}>
                    {idSchemaArray?.map((file: string) => {
                        return (
                            <FormGroup
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    width: '100%',
                                    margin: '10px 0px'
                                }}
                                key={file + Math.random()}
                            >
                                <TextField
                                    type="text"
                                    disabled={true}
                                    style={{ width: 350, margin: 0 }}
                                    label={file}
                                    value={files[file]}
                                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                        handleChange(file, event)
                                    }
                                />

                                <Button
                                    variant="contained"
                                    color="primary"
                                    style={{ marginLeft: 25 }}
                                    onClick={() => {
                                        setSelectedInput(file)
                                        return setGalleryState({ ...state, openGallery: true })
                                    }}
                                >
                                    Select file
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    style={{ marginLeft: 25 }}
                                    onClick={() => {
                                        setFiles({ ...files, [file]: '' })
                                    }}
                                >
                                    Remove
                                </Button>
                            </FormGroup>
                        )
                    })}
                </FormControl>
            </React.Fragment>
        )
    }

    const hiddenNodes = JSON.parse(process.env.REACT_APP_HIDDEN_CONFIGURATOR_NODES || '[]')
    const hiddenNodesUiSchema: UiSchema = {}
    for (const node of hiddenNodes) {
        const nodePath = node.split('.')
        let currentUiSchema = hiddenNodesUiSchema

        for (const pathSegment of nodePath) {
            currentUiSchema[pathSegment] = currentUiSchema[pathSegment] || {}
            currentUiSchema = currentUiSchema[pathSegment]
        }
        currentUiSchema['ui:widget'] = 'hidden'
    }

    let uiSchemaObject: UiSchema = (uiSchema && JSON.parse(uiSchema)) || {}

    if (uiSchemaObject) {
        uiSchemaObject = {
            ...uiSchemaObject,
            icon: {
                'ui:field': IconFieldTemplate
            },
            ...{
                code: { 'ui:readonly': true },
                version: { 'ui:readonly': true }
            },
            ...hiddenNodesUiSchema
        }
        uiSchemaObject.background = {
            'ui:field': IconFieldTemplate
        }
        uiSchemaObject.printing_canvas_url = {
            'ui:field': IconFieldTemplate
        }

        // Configure dependency path fields to use the DependencyPathSelector
        // Apply to any field named "path" in the schema
        const applyPathSelectorRecursively = (schema: any, uiSchema: any) => {
            if (schema?.properties) {
                Object.keys(schema.properties).forEach((key) => {
                    if (key === 'path' && schema.properties[key]?.type === 'string') {
                        // Apply DependencyPathSelector to any string field named "path"
                        uiSchema[key] = { 'ui:widget': DependencyPathSelector }
                    } else if (
                        schema.properties[key]?.type === 'object' ||
                        schema.properties[key]?.type === 'array'
                    ) {
                        // Recursively apply to nested objects and arrays
                        if (!uiSchema[key]) uiSchema[key] = {}
                        if (
                            schema.properties[key]?.type === 'array' &&
                            schema.properties[key]?.items
                        ) {
                            if (!uiSchema[key].items) uiSchema[key].items = {}
                            applyPathSelectorRecursively(
                                schema.properties[key].items,
                                uiSchema[key].items
                            )
                        } else {
                            applyPathSelectorRecursively(schema.properties[key], uiSchema[key])
                        }
                    }
                })
            }
        }

        // Apply path selector to any path fields in the schema
        if (nodeSchema) {
            applyPathSelectorRecursively(nodeSchema, uiSchemaObject)
        }

        // Also add specific configurations for known dependency patterns
        if (uiSchemaObject.dependencies) {
            uiSchemaObject.dependencies = {
                ...uiSchemaObject.dependencies,
                rules: {
                    ...uiSchemaObject.dependencies.rules,
                    items: {
                        ...uiSchemaObject.dependencies.rules?.items,
                        path: {
                            'ui:widget': DependencyPathSelector
                        }
                    }
                }
            }
        }

        // Handle direct path field (commonly used)
        if (nodeSchema?.properties?.path) {
            uiSchemaObject.path = {
                'ui:widget': DependencyPathSelector
            }
        }

        // Process all array fields recursively
        uiSchemaObject = nodeSchema && wrapAccordionAroundArrayFields(nodeSchema, uiSchemaObject)
    }

    useEffect(() => {
        // To prevent form caching we need this to update the forms correctly.
        setUniqueKey(Date.now())
        setFormData(node && JSON.parse(node?.node_attributes || ''))

        return () => {
            setFormData({})
        }
    }, [node])

    const OnChangeHandler = (e: IChangeEvent<any, RJSFSchema, any>) => {
        setFormData(e.formData)
    }

    const OnSaveHandler = (e: IChangeEvent<any, RJSFSchema, any>) => {
        if (affectedPresetsIDs.length) {
            setFormEvent(e)
            setIsActiveAffectedPresetsWarningPopup(true)
            return
        }
        submitForm(e)
    }

    const TransformErrors = (errors: RJSFValidationError[]) => {
        return errors?.map((error: RJSFValidationError) => {
            console.warn(error)
            return error
        })
    }

    const OnErrorHandler = (errors: JSONSchema7Object[]) => {
        setErrorState({ hasError: true, message: errors[0].stack as string })
    }

    return loading || uiSchemaLoading ? (
        <CircularProgress />
    ) : (
        <>
            {nodeSchema && node?.node_attributes && (
                <>
                    <Typography
                        variant="h5"
                        style={{
                            marginBottom: -60,
                            paddingTop: 10,
                            zIndex: 98,
                            fontWeight: 'bold',
                            position: 'sticky',
                            top: '0',
                            backgroundColor: lightTheme.palette?.background?.default,
                            width: '100%',
                            height: '100px'
                        }}
                    >
                        {JSON.parse(node?.node_attributes)?.label ??
                            translate('manager.resources.model_manager.root_node')}
                    </Typography>
                    <StyledForm
                        showErrorList={false}
                        key={uniqueKey}
                        schema={nodeSchema}
                        uiSchema={uiSchemaObject}
                        formData={formData}
                        onSubmit={(e) => OnSaveHandler(e)}
                        onChange={(e) => OnChangeHandler(e)}
                        onError={(e) => OnErrorHandler(e)}
                        transformErrors={TransformErrors}
                        validator={validator}
                    >
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            style={{
                                marginBottom: 15,
                                zIndex: 99,
                                position: 'sticky',
                                top: '50px'
                            }}
                        >
                            {translate('manager.resources.general.save')}
                        </Button>
                    </StyledForm>
                    <WarningDialog
                        open={isActiveAffectedPresetsWarningPopup}
                        handleClose={handleCloseAffectedPresetsWarningPopup}
                        handleContinue={handleContinueAffectedPresetsWarningPopup}
                        maxWidth="md"
                        title="Affected presets"
                        content="Editing the node will affect on these presets:"
                        JSXContent={<AffectedPresetTable affectedPresetsIDs={affectedPresetsIDs} />}
                    />
                </>
            )}
        </>
    )
}

export default AttributesEditor
